import { RIS_BASE_URL } from 'config';
import { UserTypeEnum } from 'types/enums/User';

// Path for the Resideo Integration Services (RIS) public signup endpoint.
const SIGNUP_PATH = '/api/v1/public/signup';

export type SignupUserType =
  | UserTypeEnum.CONSUMER
  | UserTypeEnum.PARTNER
  | UserTypeEnum.DISTRIBUTOR;

export interface SignupPayload {
  contactEmail: string;
  password: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  language: string;
  signUpUserType: SignupUserType;
  stackIdentifier: string;
  inviteId?: string;
  returnToUrl?: string;
  signUpPartnerGuest?: string;
  signUpApp?: string;
  phoneNumber?: string;
  isInAppWebview?: boolean;
}

export interface BuildSignupPayloadInput {
  values: {
    email?: string;
    password?: string;
    firstName?: string;
    lastName?: string;
    country?: string;
    phoneNumber?: string;
  };
  userType: UserTypeEnum | undefined;
  stackIdentifier: string;
  inviteToken?: string | null;
  returnToUrl?: string;
  signUpPartnerGuest?: string;
  signUpApp?: string;
  isInAppWebview?: boolean;
}

/**
 * Builds the RIS signup request body. Optional fields flattened from the former
 * Auth0 `metadata` block are omitted entirely when empty — the backend deserializes
 * `signUpApp`/`signUpPartnerGuest`/`stackIdentifier` as enums and rejects empty
 * strings, so we must send the key only when it carries a real value.
 */
export const buildSignupPayload = ({
  values,
  userType,
  stackIdentifier,
  inviteToken,
  returnToUrl,
  signUpPartnerGuest,
  signUpApp,
  isInAppWebview,
}: BuildSignupPayloadInput): SignupPayload => ({
  contactEmail: (values.email || '').trim(),
  password: values.password || '',
  firstName: (values.firstName || '').trim(),
  lastName: (values.lastName || '').trim(),
  countryCode: (values.country || '').toUpperCase(),
  language: window.navigator.language || 'en-US',
  signUpUserType: userType ?? UserTypeEnum.PARTNER,
  stackIdentifier,
  // Optional fields: omit when empty so enum fields are never sent as "".
  ...(inviteToken ? { inviteId: inviteToken } : {}),
  ...(returnToUrl ? { returnToUrl } : {}),
  ...(signUpPartnerGuest ? { signUpPartnerGuest } : {}),
  ...(signUpApp ? { signUpApp } : {}),
  ...(values.phoneNumber ? { phoneNumber: values.phoneNumber } : {}),
  ...(isInAppWebview ? { isInAppWebview: true } : {}),
});

export interface SignupErrorDetail {
  code?: string;
  message?: string;
  reasonCode?: string;
}

export interface SignupSuccess {
  data: { email?: string; [key: string]: unknown };
  errors: SignupErrorDetail[];
}

// The category of failure, mapped from the HTTP status so the UI can decide
// how to respond without depending on backend-specific messages.
export type SignupErrorKind =
  | 'validation'
  | 'recaptcha'
  | 'conflict'
  | 'unavailable'
  | 'not_found'
  | 'network';

interface SignupApiErrorOptions {
  status?: number;
  code?: string;
  errors?: SignupErrorDetail[];
  message?: string;
}

export class SignupApiError extends Error {
  readonly kind: SignupErrorKind;
  readonly status?: number;
  readonly code?: string;
  readonly errors: SignupErrorDetail[];

  constructor(kind: SignupErrorKind, options: SignupApiErrorOptions = {}) {
    super(options.message ?? kind);
    this.name = 'SignupApiError';
    this.kind = kind;
    this.status = options.status;
    this.code = options.code;
    this.errors = options.errors ?? [];
  }
}

const kindForStatus = (status: number): SignupErrorKind => {
  switch (status) {
    case 400:
      return 'validation';
    case 403:
      return 'recaptcha';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 503:
      return 'unavailable';
    default:
      return 'network';
  }
};

/**
 * Registers a new user against the RIS public signup API.
 *
 * The endpoint is anonymous (no bearer token) but requires a fresh, single-use
 * reCAPTCHA Enterprise token supplied via the `X-Recaptcha-Token` header. The
 * caller is responsible for generating a new token for each attempt. The
 * ResideoId Auth0 `clientId` is passed via the `X-Client-Id` header so the
 * backend can resolve the correct redirect client for the verification email.
 *
 * On success (HTTP 201) the parsed `{ data, errors }` envelope is returned.
 * All non-2xx responses throw a {@link SignupApiError} whose `kind` is derived
 * from the HTTP status so callers can render the correct UI state.
 *
 * Note: this function never logs the payload (PII) or the reCAPTCHA token.
 */
export const signupUser = async (
  payload: SignupPayload,
  recaptchaToken: string,
  clientId: string
): Promise<SignupSuccess> => {
  let response: Response;

  try {
    response = await fetch(`${RIS_BASE_URL}${SIGNUP_PATH}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Recaptcha-Token': recaptchaToken,
        'X-Client-Id': clientId,
      },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new SignupApiError('network', {
      message: 'Signup request failed to reach the server',
    });
  }

  if (response.status === 201) {
    try {
      return (await response.json()) as SignupSuccess;
    } catch {
      // A 201 with an empty or non-JSON body is still a success.
      return { data: {}, errors: [] };
    }
  }

  const body = (await response.json().catch(() => undefined)) as
    | { errors?: SignupErrorDetail[]; code?: string }
    | undefined;

  const errors = body?.errors ?? [];
  const code = body?.code ?? errors[0]?.code;

  throw new SignupApiError(kindForStatus(response.status), {
    status: response.status,
    code,
    errors,
  });
};
